import { getCDNImageURL } from "./getCDNImageURL";
import { getHostname } from "./hostname";

/**
 * Create a standardized poster image from an input image.
 *
 * This uses a canvas to redraw the image to the desired size, aspect ratio, and effect.
 * It exports the image as a base64 string.
 *
 * - If the image matches the aspect ratio, the image will be returned as is.
 * - If the image does not match the aspect ratio, the image will be either
 *   scaled to *fit* or *fill* the canvas.
 * - If *fit* is used, the image will be scaled to fit the canvas, and a blurred
 *   version of the image will be drawn in the background to fill the remaining
 *   space.
 *
 * @param imgSrc - The source image URL or base64 string
 * @param size - The size of the poster image
 * @param aspectMode - The aspect mode of the poster image
 * @returns A promise that resolves to the poster image as a base64 string
 */
export function createPosterImage({
  imgSrc,
  size,
  aspectMode,
}: {
  imgSrc: string;
  size: "16:9" | "1:1";
  aspectMode: "fit" | "fill";
}): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return resolve(imgSrc);
    }

    if (size === "16:9") {
      canvas.width = 1920;
      canvas.height = 1080;
    } else {
      canvas.width = 1000;
      canvas.height = 1000;
    }
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const img = new Image();
    img.onload = () => {
      const imgAspectRatio = img.width / img.height;
      const canvasAspectRatio = canvas.width / canvas.height;

      let drawWidth, drawHeight;

      if (Math.abs(imgAspectRatio - canvasAspectRatio) < 0.01) {
        return resolve(imgSrc);
      }

      if (imgAspectRatio > canvasAspectRatio) {
        // Image is wider relative to canvas
        if (aspectMode === "fit") {
          drawWidth = canvasWidth;
          drawHeight = canvasWidth / imgAspectRatio;
        } else {
          drawWidth = canvasHeight * imgAspectRatio;
          drawHeight = canvasHeight;
        }
      } else {
        // Image is taller relative to canvas
        if (aspectMode === "fit") {
          drawHeight = canvasHeight;
          drawWidth = canvasHeight * imgAspectRatio;
        } else {
          drawHeight = canvasHeight / imgAspectRatio;
          drawWidth = canvasWidth;
        }
      }
      const x = (canvasWidth - drawWidth) / 2;
      const y = (canvasHeight - drawHeight) / 2;

      // Draw blur version
      ctx.globalAlpha = 0.8;
      ctx.filter = "blur(20px)";
      ctx.drawImage(img, -20, -20, canvasWidth + 40, canvasHeight + 40);
      ctx.filter = "none";
      ctx.globalAlpha = 1;

      // Draw normal version centered in canvas
      ctx.drawImage(img, x, y, drawWidth, drawHeight);
      resolve(canvas.toDataURL());
    };
    img.src = imgSrc;
    img.onerror = () => {
      console.log("Failed to load image");
      reject(new Error("Failed to load image"));
    };
  });
}

/**
 * Crop an image to a specified region.
 *
 * @param imgSrc - The source image URL or base64 string
 * @param crop - The region to crop. Designed to work with react-easy-crop's
 *   cropping tool.
 * @returns A promise that resolves to the cropped image as a base64 string
 */
export function cropImage({
  imgSrc,
  crop,
}: {
  imgSrc: string;
  crop: { x: number; y: number; width: number; height: number };
}): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const cropX = (crop.x / 100) * img.width;
      const cropY = (crop.y / 100) * img.height;
      const cropWidth = (crop.width / 100) * img.width;
      const cropHeight = (crop.height / 100) * img.height;

      const canvas = document.createElement("canvas");
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Failed to get canvas context"));

      ctx.drawImage(
        img,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      resolve(canvas.toDataURL());
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = imgSrc;
  });
}

export function getLogoURL(): string {
  return getCDNImageURL("bddcbddf-002c-4b03-8f3d-27be2303f300");
}

export function getLogoWithIconURL(): string {
  return getCDNImageURL("517feabd-b6f2-46a6-eef7-4ac2efba7400");
}

export const SupportedColors = [
  "FFFFFF",
  "BBBBBB",
  "4E4E4E",
  "000000",
  "3698F3",
  "70C04F",
  "FDCB5C",
  "FC8D33",
  "FF5E26",
  "EE4957",
  "D10868",
  "A207BA",
  "FFD7CD",
  "ED5C61",
  "D74146",
  "FEDBB3",
  "D29046",
  "99643A",
  "432324",
  "1F4828",
];
/**
 * CompositorImageLayout
 *
 *   Specifies where the text on the image should be positioned. Think of the numeric values like a phone keypad, ie..
 *   1 | 2 | 3
 *   4 | 5 | 6
 *   7 | 8 | 9
 *   Where 1 is the top left, 9 is the bottom right (5 is not used yet).
 *   The enum values allow us to extend this with additional layout options in the future.
 */
export type CompositorImageLayout = 1 | 2 | 3 | 4 | 6 | 7 | 8 | 9;

export function getCompositorImageConfigParamString({
  cloudflareImageId,
  comedianName,
  specialTitle,
  fontFamily,
  layout,
  textColor,
}: {
  cloudflareImageId: string;
  comedianName: string;
  specialTitle: string;
  fontFamily: string;
  layout: CompositorImageLayout;
  textColor: string; // Hex color code, without the # symbol
}) {
  const layers = [];
  if (layout === 1) {
    layers.push({
      text: specialTitle,
      font: fontFamily,
      fontSize: 100,
      fontWeight: "bold",
      fill: textColor,
      x: "2%",
      y: "15%",
      textAnchor: "start",
      rotate: 0,
    });
    layers.push({
      text: comedianName,
      font: fontFamily,
      fontSize: 60,
      fontWeight: "bold",
      fill: textColor,
      x: "2%",
      y: "28%",
      textAnchor: "start",
      rotate: 0,
    });
  } else if (layout === 2) {
    layers.push({
      text: specialTitle,
      font: fontFamily,
      fontSize: 100,
      fontWeight: "bold",
      fill: textColor,
      x: "50%",
      y: "15%",
      textAnchor: "middle",
      rotate: 0,
    });
    layers.push({
      text: comedianName,
      font: fontFamily,
      fontSize: 60,
      fontWeight: "bold",
      fill: textColor,
      x: "50%",
      y: "28%",
      textAnchor: "middle",
      rotate: 0,
    });
  } else if (layout === 3) {
    layers.push({
      text: specialTitle,
      font: fontFamily,
      fontSize: 100,
      fontWeight: "bold",
      fill: textColor,
      x: "98%",
      y: "15%",
      textAnchor: "end",
      rotate: 0,
    });
    layers.push({
      text: comedianName,
      font: fontFamily,
      fontSize: 60,
      fontWeight: "bold",
      fill: textColor,
      x: "98%",
      y: "28%",
      textAnchor: "end",
      rotate: 0,
    });
  } else if (layout === 7) {
    layers.push({
      text: specialTitle,
      font: fontFamily,
      fontSize: 100,
      fontWeight: "bold",
      fill: textColor,
      x: "2%",
      y: "80%",
      textAnchor: "start",
      rotate: 0,
    });
    layers.push({
      text: comedianName,
      font: fontFamily,
      fontSize: 60,
      fontWeight: "bold",
      fill: textColor,
      x: "2%",
      y: "92%",
      textAnchor: "start",
      rotate: 0,
    });
  } else if (layout === 8) {
    layers.push({
      text: specialTitle,
      font: fontFamily,
      fontSize: 100,
      fontWeight: "bold",
      fill: textColor,
      x: "50%",
      y: "80%",
      textAnchor: "middle",
      rotate: 0,
    });
    layers.push({
      text: comedianName,
      font: fontFamily,
      fontSize: 60,
      fontWeight: "bold",
      fill: textColor,
      x: "50%",
      y: "92%",
      textAnchor: "middle",
      rotate: 0,
    });
  } else if (layout === 9) {
    layers.push({
      text: specialTitle,
      font: fontFamily,
      fontSize: 100,
      fontWeight: "bold",
      fill: textColor,
      x: "98%",
      y: "80%",
      textAnchor: "end",
      rotate: 0,
    });
    layers.push({
      text: comedianName,
      font: fontFamily,
      fontSize: 60,
      fontWeight: "bold",
      fill: textColor,
      x: "98%",
      y: "92%",
      textAnchor: "end",
      rotate: 0,
    });
  }
  return JSON.stringify({
    image: { cfImageId: cloudflareImageId },
    layers,
  });
}

export function getComedianBlurredBioImageUrl(comedianSlug: string) {
  return `${getHostname()}/api/image/comedian/${comedianSlug}`;
}

export function getPodcastBlurredImageUrl(
  comedianSlug: string,
  podcastSlug: string
) {
  return `${getHostname()}/api/v2/public/image/podcast/${comedianSlug}/${podcastSlug}`;
}
